import { computed, ref, type ComputedRef, watch, onMounted } from 'vue';
import {
  addSearch,
  buildSearchCriteria,
  buildSearchCriteriaParams,
  getHotelUrl,
  getLastSearchCriteria,
  getSingleHotelId,
  setLastSearchCriteria,
  type DestinationPrices,
} from '@/entities/searchCriteria';
import { getSearchFormData, getCountryResorts } from '@/shared/api';
import { getMinPricesByCriterias } from '@/shared/api/getMinPricesByCriterias';
import { useTravelataAppConfig } from '@/shared/composables';
import { type CountryResortsUrl, type FormDataUrl, type IntelligentSearchUrl } from '@/shared/config/formData';
import { type LocalStorageKey } from '@/shared/config/localStorage';
import { PagesUrl, SEARCH_SOURCE_BY_PAGE, type ProductType } from '@/shared/config/pages';
import { type Country, type Resort, type SearchFormData } from '@/shared/model';
import type {
  DateRange,
  DepartureCity,
  NightsRange,
  Selection,
  SearchCriteria,
  TouristsSelection,
  SearchCriteriaParams,
} from 'src';

type Options = {
  isOpen: ComputedRef<boolean>;
  formUrl?: FormDataUrl;
  searchUrl?: IntelligentSearchUrl;
  resortsUrl?: CountryResortsUrl;
  to: PagesUrl;
  productType?: ProductType;
  lastCriteriaKey?: LocalStorageKey;
  withPrices?: boolean;
  /** Записывать поиск в историю (`tr_historySearch`). Только для тур-продукта. */
  withHistory?: boolean;
  getUrl: (searchCriteria: SearchCriteria) => string;
};

type CriteriasWithIndex = {
  requests: SearchCriteriaParams[];
  indexToId: Record<string, number>;
};

async function resolveAllResorts(
  country: Country | null | undefined,
  resorts: Resort[],
  resortsUrl?: string,
): Promise<Resort[]> {
  const countryResorts = await getCountryResorts(country, resortsUrl);
  const popularCountryResorts = countryResorts.filter((resort) => resort.isPopular);

  return [...popularCountryResorts, ...resorts];
}

function buildCountryCriterias(countries: (Country | null | undefined)[], params: SearchCriteria): CriteriasWithIndex {
  const indexToId: Record<string, number> = {};
  const requests = countries
    .filter((country): country is Country => Boolean(country && country.isPopular))
    .reduce<SearchCriteriaParams[]>((acc, country, index) => {
      indexToId[index] = country.id;
      acc.push(
        buildSearchCriteriaParams({
          ...params,
          destination: { country, kind: 'country' },
        }),
      );

      return acc;
    }, []);

  return { requests, indexToId };
}

function buildResortCriterias(
  resorts: Resort[],
  country: Country | null | undefined,
  params: SearchCriteria,
): CriteriasWithIndex {
  const indexToId: Record<string, number> = {};
  const requests = resorts.reduce<SearchCriteriaParams[]>((acc, resort, index) => {
    indexToId[index] = resort.id;
    acc.push(
      buildSearchCriteriaParams({
        ...params,
        destination: { country: country!, resorts: [resort], kind: 'resorts' },
      }),
    );

    return acc;
  }, []);

  return { requests, indexToId };
}

function applyMinPrices(
  prices: DestinationPrices,
  result: Record<number, number>,
  indexToId: Record<string, number>,
  section: 'country' | 'resort',
): DestinationPrices {
  const sectionPrices: Record<number, number> = { ...prices[section] };

  Object.entries(result).forEach(([index, value]) => {
    sectionPrices[indexToId[index]] = value;
  });

  return { ...prices, [section]: sectionPrices };
}

export default function useFormModel(options: Options) {
  const { formUrl, searchUrl, resortsUrl, to, getUrl } = options;
  const appConfig = useTravelataAppConfig();
  const prices = ref<DestinationPrices>({ country: {}, resort: {} });
  const dateRange = ref<DateRange | null>(null);
  const tourists = ref<TouristsSelection | null>(null);
  const nights = ref<NightsRange | null>(null);
  const fromCity = ref<DepartureCity | null>(null);
  const destination = ref<Selection | null>(null);

  const model = computed<SearchCriteria>({
    get: () => {
      return {
        nights: nights.value,
        dateRange: dateRange.value,
        fromCity: fromCity.value,
        destination: destination.value,
        tourists: tourists.value,
      };
    },
    set: (searchCriteria: SearchCriteria) => {
      nights.value = searchCriteria.nights;
      dateRange.value = searchCriteria.dateRange;
      fromCity.value = searchCriteria.fromCity;
      destination.value = searchCriteria.destination;
      tourists.value = searchCriteria.tourists;
    },
  });

  function onSearch(searchCriteria: SearchCriteria): void {
    if (options.lastCriteriaKey) {
      setLastSearchCriteria(options.lastCriteriaKey, searchCriteria);
    }
    if (options.withHistory) {
      addSearch(searchCriteria);
    }

    const hotelId = getSingleHotelId(searchCriteria.destination);
    if (hotelId) {
      const hotelUrl = getHotelUrl(searchCriteria, {
        productType: options.productType,
        searchSource: SEARCH_SOURCE_BY_PAGE[to],
      });
      window.location.href = `${appConfig.baseHost}/${PagesUrl.GotoHotel}/${hotelId}#?${hotelUrl}`;
      return;
    }

    const url = getUrl(searchCriteria);
    window.location.href = `${appConfig.baseHost}/${to}#?${url}`;
  }

  const setMinPricesByCriterias = async (
    country: Country | null | undefined,
    resorts: Resort[],
    params: SearchCriteria,
    formData: SearchFormData,
  ) => {
    const allResorts = await resolveAllResorts(country, resorts, resortsUrl);

    const { requests: countryRequests, indexToId: countryIndexToId } = buildCountryCriterias(
      [...formData.countries, country],
      params,
    );
    const { requests: resortRequests, indexToId: resortIndexToId } = buildResortCriterias(allResorts, country, params);

    const [countriesResult, resortsResult] = await Promise.all([
      getMinPricesByCriterias(countryRequests),
      getMinPricesByCriterias(resortRequests),
    ]);

    prices.value = applyMinPrices(prices.value, countriesResult, countryIndexToId, 'country');
    prices.value = applyMinPrices(prices.value, resortsResult, resortIndexToId, 'resort');
  };

  onMounted(() => {
    watch(
      options.isOpen,
      async () => {
        if (options.isOpen.value) {
          if (destination.value) return;
          const lastSearchCriteria = getLastSearchCriteria(options.lastCriteriaKey);
          const formData = await getSearchFormData(formUrl);
          const params = await buildSearchCriteria(lastSearchCriteria, formData, resortsUrl);
          const resorts = params?.destination?.kind === 'resorts' ? params.destination.resorts : [];
          const country = params.destination?.kind === 'none' ? null : params.destination?.country;

          model.value = {
            ...params,
          };
          if (!options.withPrices) return;
          setMinPricesByCriterias(country, resorts, params, formData);
        }
      },
      { immediate: true },
    );
  });

  return {
    onSearch,
    model,
    formUrl,
    searchUrl,
    resortsUrl,
    prices,
  };
}
